import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ThemeReferentiel } from '@common/model/theme-ref.enum';
import { UserProfile, UserProfileDetailed } from '@common/model/user-profile.model';
import { ThemeService } from '@core/service/theme.service';
import { environment } from '@environments/environment';
import { DelegationInfo, DelegationState, DelegationSwitchRequest, DelegationTokenResponse } from '@shared/model/delegation.model';
import { KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, catchError, Observable, of, tap, throwError } from 'rxjs';
import { AuthDelegationService } from './auth-delegation.service';

const ROLE_ADMIN = 'ROLE_SUPER';

export type EntityArrayResponseType = HttpResponse<any[]>;
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private resourceUrl = `${environment.SERVER_UTILISATEUR_API_URL}/api/account`;

  private userInfo$ = new BehaviorSubject<UserProfile | undefined>(undefined);
  private authInitialized$ = new BehaviorSubject<boolean>(false);

  private doLogOut = false;

  // Delegation state
  private isDelegating$ = new BehaviorSubject<boolean>(false);

  private delegationToken: string | null = null;
  private originalUserProfile: UserProfile | null = null;

  constructor(
    private keycloakService: KeycloakService,
    protected http: HttpClient,
    private authDelegationService: AuthDelegationService,
    private themeService: ThemeService
  ) {
    this.handleAuthEvents();
    this.loadUserInfo(keycloakService.isLoggedIn());
  }

  private handleAuthEvents() {
    this.keycloakService.keycloakEvents$.subscribe({
      next: (event) => {
        if (!this.isAuthenticated() && event.type == KeycloakEventType.OnAuthSuccess) {
          this.loadUserInfo(true);
        } else if (this.isAuthenticated() && event.type == KeycloakEventType.OnAuthLogout) {
          this.loadUserInfo(false);
        } else if (event.type === KeycloakEventType.OnAuthRefreshError || event.type === KeycloakEventType.OnTokenExpired) {
          this.doLogOut = true;
          setTimeout(() => {
            if (this.doLogOut) {
              this.loadUserInfo(false);
              this.logout();
            }
          }, 30000);
        } else if (event.type === KeycloakEventType.OnAuthRefreshSuccess) {
          this.doLogOut = false;
        }
      },
    });
  }

  private loadUserInfo(authenticated: boolean) {
    if (authenticated) {
      // this.userInfo$.next({
      //   userName: 'super',
      //   userFullName: 'admin test',
      //   lang: 'fr',
      //   userFullNameAr: 'admin test',
      //   userRoles: ['ROLE_SUPER'],
      //   userRights: [],
      // });
      this.loadProfile().subscribe({
        next: (up: UserProfile) => {
          up.roles = up.roles ?? [];
          up.ressources = up.ressources ?? [];
          this.userInfo$.next(up);
          this.authInitialized$.next(true);
          console.log('up.theme', up.theme);
          this.themeService.setTheme(up.theme);
        },
      });
    } else {
      this.userInfo$.next(undefined);
      this.authInitialized$.next(true);
      this.themeService.setTheme(ThemeReferentiel.Default);
    }
  }

  getRedirectUri() {
    let port = location.port ? ':' + location.port : '';
    return location.protocol + '//' + location.hostname + port + '/';
  }

  logout() {
    this.userInfo$.next(undefined);
    this.keycloakService.logout(this.getRedirectUri());
  }

  profile() {
    this.keycloakService.getKeycloakInstance().accountManagement();
  }

  changePassword(): void {
    this.keycloakService.login({
      action: 'UPDATE_PASSWORD',
      redirectUri: this.getRedirectUri(),
    });
  }

  getRoles() {
    if (!this.isAuthenticated()) {
      return [];
    }
    return this.keycloakService.getUserRoles();
  }

  getProfile(): Observable<UserProfile | undefined> {
    return this.userInfo$.asObservable();
  }

  afterInit(): Observable<boolean> {
    return this.authInitialized$.asObservable();
  }

  isAuthenticated() {
    return this.keycloakService.isLoggedIn();
  }

  isSuperAdmin() {
    if (!this.isAuthenticated()) {
      return false;
    }
    return this.isUserInRole(ROLE_ADMIN);
  }

  isUserInRole(role: string) {
    let up = this.getProfileSync();
    if (!up) {
      return false;
    }
    return up.roles.includes(role);
  }

  getProfileSync(): UserProfile | undefined {
    return this.userInfo$.getValue();
  }

  hasAnyRole(authorities: string[]): boolean {
    if (!this.getProfileSync()) {
      return false;
    }
    if (this.isSuperAdmin()) {
      return true;
    }
    return authorities.some((role) => this.isUserInRole(role));
  }

  hasAnyAccess(accesses: string[]): boolean {
    //var access =  accesses.reduce((a, v) => ({ ...a, ['appRightName']: v}), {});
    let up = this.getProfileSync();
    if (!up) {
      return false;
    }
    if (this.isSuperAdmin()) {
      return true;
    }
    return accesses.some((access) => up?.ressources.includes(access));
  }

  loadProfile(): Observable<UserProfile> {
    return this.http.get<any>(`${this.resourceUrl}`, { observe: 'body' });
  }

  getUserProfiles(): Observable<UserProfileDetailed[]> {
    return this.http.get<UserProfileDetailed[]>(`${this.resourceUrl}`, {
      observe: 'body',
    });
  }

  // Switch to delegation mode
  switchToDelegation(delegationId: string): Observable<DelegationTokenResponse> {
    const request: DelegationSwitchRequest = { delegationId };

    return this.http.post<DelegationTokenResponse>(`${this.resourceUrl}/delegation/switch`, request).pipe(
      tap((response) => {
        this.handleDelegationSwitch(response);
      }),
      catchError((error) => {
        console.error('Error switching to delegation:', error);
        return throwError(() => error);
      })
    );
  }

  // Handle successful delegation switch
  private handleDelegationSwitch(response: DelegationTokenResponse) {
    const delegationState: DelegationState = {
      isDelegating: true,
      delegationId: response.delegationId,
      delegatorName: response.delegatorName, // From API response
      originalUser: this.originalUserProfile?.login || '',
      expiresAt: new Date(response.expiresAt),
    };

    this.authDelegationService.setDelegationState(delegationState);

    // Load fresh delegated user profile
    this.reloadUserInfo();
  }

  clearDelegationState() {
    this.authDelegationService.clearDelegationState();
    this.reloadUserInfo();
  }

  reloadUserInfo() {
    this.loadUserInfo(this.isAuthenticated());
  }

  createDelegation(delegataireLogin: string, dateDebut: any, dateFin: any, delegationReason: string, utilisateurId: number = undefined): Observable<DelegationInfo> {
    const createDelegationDTO = {
      delegataireLogin,
      dateDebut: dateDebut,
      dateFin: dateFin,
      delegationReason,
      utilisateurId,
    };

    return this.http.post<DelegationInfo>(`${this.resourceUrl}/delegations`, createDelegationDTO).pipe(
      catchError((error) => {
        console.error('Error creating delegation:', error);
        return throwError(() => error);
      })
    );
  }

  revokeDelegation(delegationId: string): Observable<void> {
    return this.http.delete<void>(`${this.resourceUrl}/delegations/${delegationId}`).pipe(
      catchError((error) => {
        console.error('Error revoking delegation:', error);
        return throwError(() => error);
      })
    );
  }

  // Get available delegations for current user (where current user is delegataire)
  getAvailableDelegations(): Observable<DelegationInfo[]> {
    return this.http.get<DelegationInfo[]>(`${this.resourceUrl}/delegations/available`).pipe(
      catchError((error) => {
        console.error('Error fetching available delegations:', error);
        return of([]);
      })
    );
  }

  // Get delegations created by current user (where current user is delegator)
  getCreatedDelegations(): Observable<DelegationInfo[]> {
    return this.http.get<DelegationInfo[]>(`${this.resourceUrl}/delegations/created`).pipe(
      catchError((error) => {
        console.error('Error fetching created delegations:', error);
        return of([]);
      })
    );
  }

  updateUserTheme(newTheme: ThemeReferentiel): void {
    // Correction ici
    const currentUser = this.userInfo$.getValue();
    if (currentUser) {
      const updatedUser = { ...currentUser, theme: newTheme };
      this.userInfo$.next(updatedUser); // Mettre à jour l'état local de l'utilisateur
      this.themeService.setTheme(newTheme); // Appliquer le nouveau thème via le service de thème
    }
  }
}
